/* ============================================================
   MICROLUX — Core JS
   Reconstructed · August 2026
   Covers: navigation, mobile drawer, scroll-reveal,
           FAQ accordion, Ascent animation, year stamp
   ============================================================ */

(function () {
  'use strict';

  /* ── Helpers ─────────────────────────────────────────────── */
  function qs(sel, ctx)  { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  /* ── 1. Auto-fill current year ───────────────────────────── */
  function initYear() {
    qsa('[data-year]').forEach(function (el) {
      el.textContent = new Date().getFullYear();
    });
  }

  /* ── 2. Navigation — scroll class ───────────────────────── */
  function initNavScroll() {
    var nav = qs('.nav');
    if (!nav) return;

    var THRESHOLD = 40;
    var ticking = false;

    function update() {
      if (window.scrollY > THRESHOLD) {
        nav.classList.add('scrolled');
      } else {
        nav.classList.remove('scrolled');
      }
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });

    update(); // run once on load
  }

  /* ── 3. Mobile drawer — hamburger toggle ─────────────────── */
  function initMobileDrawer() {
    var burger = qs('.nav__burger');
    var drawer = qs('.drawer');
    if (!burger || !drawer) return;

    function close() {
      burger.classList.remove('is-open');
      drawer.classList.remove('is-open');
      burger.setAttribute('aria-expanded', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      document.body.style.overflow = '';
    }

    function open() {
      burger.classList.add('is-open');
      drawer.classList.add('is-open');
      burger.setAttribute('aria-expanded', 'true');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.removeAttribute('inert');
      document.body.style.overflow = 'hidden';
    }

    burger.setAttribute('aria-label', 'Open navigation');
    burger.setAttribute('aria-expanded', 'false');
    burger.setAttribute('aria-controls', 'mobile-drawer');
    drawer.id = 'mobile-drawer';

    burger.addEventListener('click', function () {
      if (drawer.classList.contains('is-open')) {
        close();
      } else {
        open();
      }
    });

    // Close on drawer link click
    qsa('a', drawer).forEach(function (a) {
      a.addEventListener('click', close);
    });

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        close();
        burger.focus();
      }
    });

    // Close on outside click
    document.addEventListener('click', function (e) {
      if (
        drawer.classList.contains('is-open') &&
        !drawer.contains(e.target) &&
        !burger.contains(e.target)
      ) {
        close();
      }
    });
  }

  /* ── 4. Desktop dropdown — keyboard support ──────────────── */
  function initDropdowns() {
    qsa('.nav__item').forEach(function (item) {
      var link    = qs('.nav__link', item);
      var dropdown = qs('.dropdown', item);
      if (!link || !dropdown) return;

      link.setAttribute('aria-haspopup', 'true');
      link.setAttribute('aria-expanded', 'false');

      item.addEventListener('mouseenter', function () {
        link.setAttribute('aria-expanded', 'true');
      });
      item.addEventListener('mouseleave', function () {
        link.setAttribute('aria-expanded', 'false');
      });

      // Toggle on click/Enter for keyboard users
      link.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          var open = dropdown.style.opacity === '1';
          if (open) {
            dropdown.style.opacity = '';
            dropdown.style.pointerEvents = '';
            dropdown.style.transform = '';
            link.setAttribute('aria-expanded', 'false');
          } else {
            dropdown.style.opacity = '1';
            dropdown.style.pointerEvents = 'all';
            dropdown.style.transform = 'translateY(0)';
            link.setAttribute('aria-expanded', 'true');
            // Focus first link
            var firstLink = qs('a', dropdown);
            if (firstLink) firstLink.focus();
          }
        }
      });

      // Close on Escape within dropdown
      dropdown.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          dropdown.style.opacity = '';
          dropdown.style.pointerEvents = '';
          dropdown.style.transform = '';
          link.setAttribute('aria-expanded', 'false');
          link.focus();
        }
      });
    });
  }

  /* ── 5. Scroll-reveal (IntersectionObserver) ─────────────── */
  function initReveal() {
    var elements = qsa('.reveal');
    if (!elements.length) return;

    // If user prefers reduced motion, make all visible immediately
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      elements.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      // Fallback: show everything
      elements.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.08
    });

    elements.forEach(function (el) { observer.observe(el); });
  }

  /* ── 6. FAQ Accordion ────────────────────────────────────── */
  function initFaq() {
    // Build FAQ items from existing markup if using .faq-list/.faq-item pattern
    qsa('.faq-item').forEach(function (item) {
      var q = qs('.faq-q', item);
      var a = qs('.faq-a', item);
      if (!q || !a) return;

      var inner = qs('.faq-a-inner', item);

      q.setAttribute('role', 'button');
      q.setAttribute('tabindex', '0');
      q.setAttribute('aria-expanded', 'false');

      function toggle() {
        var open = item.classList.contains('is-open');
        if (open) {
          item.classList.remove('is-open');
          a.style.maxHeight = '0';
          q.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('is-open');
          a.style.maxHeight = (inner ? inner.offsetHeight : a.scrollHeight) + 'px';
          q.setAttribute('aria-expanded', 'true');
        }
      }

      q.addEventListener('click', toggle);
      q.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      });
    });

    // Also handle simple <details>/<summary> elements if used
    qsa('details.faq-item').forEach(function (details) {
      // Native details already handle open/close — just add class sync
      details.addEventListener('toggle', function () {
        details.classList.toggle('is-open', details.open);
      });
    });
  }

  /* ── 7. Ascent deploy/retract animation ──────────────────── */
  function initAscent() {
    var stage  = qs('[data-ascent-stage]');
    var toggle = qs('[data-ascent-toggle]');
    if (!stage || !toggle) return;

    var deployed = false;

    toggle.addEventListener('click', function () {
      deployed = !deployed;
      if (deployed) {
        stage.classList.remove('is-stowed');
        stage.classList.add('is-deployed');
        toggle.textContent = 'Retract';
      } else {
        stage.classList.remove('is-deployed');
        stage.classList.add('is-stowed');
        toggle.textContent = 'Deploy';
      }
    });
  }

  /* ── 8. Smooth anchor scroll (for jump links) ────────────── */
  function initAnchorScroll() {
    var NAV_HEIGHT = 80; // px offset for sticky nav

    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href').slice(1);
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      var top = target.getBoundingClientRect().top + window.scrollY - NAV_HEIGHT;
      window.scrollTo({ top: top, behavior: 'smooth' });
    });
  }

  /* ── 9. Homepage hero — slow installation crossfade ──────── */
  function initHeroGallery() {
    qsa('[data-ml-hero-gallery]').forEach(function (gallery) {
      var slides = qsa('.ml-home-hero__slide', gallery);
      var labels = qsa('.ml-home-hero__scene-labels > span', gallery);
      if (slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      var activeIndex = 0;
      window.setInterval(function () {
        if (document.hidden) return;
        slides[activeIndex].classList.remove('is-active');
        if (labels[activeIndex]) labels[activeIndex].classList.remove('is-active');
        activeIndex = (activeIndex + 1) % slides.length;
        slides[activeIndex].classList.add('is-active');
        if (labels[activeIndex]) labels[activeIndex].classList.add('is-active');
      }, 9000);
    });
  }

  /* ── 10. Contact form — async submit (Netlify) ───────────── */
  function initContactForm() {
    var form = qs('#consult');
    var btn  = qs('#consultSend');
    if (!form || !btn) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      btn.textContent = 'Sending…';
      btn.disabled = true;

      fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(new FormData(form)).toString()
      })
        .then(function () {
          btn.textContent = 'Received — our design team will be in touch';
          btn.style.background = '#2d5a2d';
          btn.style.color = '#fff';
        })
        .catch(function () {
          btn.textContent = 'Something went wrong — please email us directly';
          btn.style.background = '#7a2020';
          btn.style.color = '#fff';
          btn.disabled = false;
        });
    });
  }

  /* ── 11. Homepage founding-price announcement — one dismissal per session ─ */
  function initLaunchOffer() {
    var offer = qs('[data-ml-launch-offer]');
    if (!offer) return;

    var dialog = qs('.ml-launch-offer__dialog', offer);
    var closeButton = qs('.ml-launch-offer__close', offer);
    var storageKey = 'microlux-launch-offer-dismissed-v1';
    var previouslyFocused = null;
    var isClosing = false;

    function wasDismissed() {
      try {
        return window.sessionStorage.getItem(storageKey) === 'true';
      } catch (error) {
        return false;
      }
    }

    function rememberDismissal() {
      try {
        window.sessionStorage.setItem(storageKey, 'true');
      } catch (error) {
        // Storage can be unavailable in strict privacy modes; closing still works.
      }
    }

    function focusableElements() {
      return qsa('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])', dialog)
        .filter(function (node) { return node.offsetParent !== null; });
    }

    function dismissOffer() {
      if (offer.hidden || isClosing) return;
      isClosing = true;
      rememberDismissal();
      offer.classList.remove('is-open');
      document.body.classList.remove('ml-launch-offer-open');

      window.setTimeout(function () {
        offer.hidden = true;
        isClosing = false;
        if (previouslyFocused && document.contains(previouslyFocused)) {
          previouslyFocused.focus({ preventScroll: true });
        }
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300);
    }

    function showOffer() {
      if (wasDismissed()) return;
      previouslyFocused = document.activeElement;
      offer.hidden = false;
      document.body.classList.add('ml-launch-offer-open');

      window.requestAnimationFrame(function () {
        offer.classList.add('is-open');
        if (closeButton) closeButton.focus({ preventScroll: true });
      });
    }

    qsa('[data-ml-launch-offer-close]', offer).forEach(function (control) {
      control.addEventListener('click', dismissOffer);
    });
    qsa('[data-ml-launch-offer-link]', offer).forEach(function (link) {
      link.addEventListener('click', dismissOffer);
    });

    offer.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismissOffer();
        return;
      }
      if (event.key !== 'Tab') return;

      var focusable = focusableElements();
      if (!focusable.length) {
        event.preventDefault();
        if (dialog) dialog.focus();
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    if (!wasDismissed()) window.setTimeout(showOffer, 1600);
  }

  /* ── Init ────────────────────────────────────────────────── */
  function init() {
    initYear();
    initNavScroll();
    initMobileDrawer();
    initDropdowns();
    initReveal();
    initFaq();
    initAscent();
    initAnchorScroll();
    initHeroGallery();
    initContactForm();
    initLaunchOffer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
